<script setup lang="ts">
/**
 * A PDF's pages turning like on the player: each page for its time (at most `maxPageMs` here, so a long document
 * previews quickly) and the PDF's page turn (cut, fade, slide or flip) between pages.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PdfPageTransition } from '@codot/contracts';

const props = withDefaults(
  defineProps<{ pages: { url: string }[]; pageMs: number; transition: PdfPageTransition; maxPageMs?: number; counter?: boolean }>(),
  { maxPageMs: 3_000, counter: true },
);
const { t } = useI18n();
const page = ref(0);
let timer: ReturnType<typeof setInterval> | undefined;
function restart(): void {
  clearInterval(timer);
  page.value = 0;
  // Not while the tab is hidden: animations don't run then, and pages would pile up mid-turn.
  if (props.pages.length > 1) timer = setInterval(() => !document.hidden && (page.value = (page.value + 1) % props.pages.length), Math.min(props.pageMs, props.maxPageMs));
}
watch(() => [props.pages, props.pageMs, props.maxPageMs], restart, { immediate: true });
onBeforeUnmount(() => clearInterval(timer));
/** Replays the turn from page 1 (the settings show a change at once). */
watch(() => props.transition, () => (page.value = props.pages.length > 1 ? 1 % props.pages.length : 0), { deep: true });

const name = computed(() => (props.transition.type === 'cut' || !props.transition.durationMs ? 'pdf-cut' : `pdf-${props.transition.type}`));
const style = computed(() => ({ '--pdf-turn': `${props.transition.durationMs}ms` }));
</script>

<template>
  <div class="relative h-full w-full overflow-hidden [perspective:1600px]" :style="style">
    <Transition :name="name">
      <img :key="page" :src="pages[page]?.url" :alt="t('content.pdfPageN', { n: page + 1 })" class="absolute inset-0 h-full w-full object-contain" />
    </Transition>
    <span v-if="counter" class="absolute bottom-2 end-2 rounded bg-black/60 px-1.5 py-0.5 text-[11px] tabular-nums text-white">{{ page + 1 }} / {{ pages.length }}</span>
  </div>
</template>

<style scoped>
.pdf-crossfade-enter-active,
.pdf-crossfade-leave-active {
  transition: opacity var(--pdf-turn) ease-in-out;
}
.pdf-crossfade-enter-from,
.pdf-crossfade-leave-to {
  opacity: 0;
}
.pdf-slide-enter-active,
.pdf-slide-leave-active {
  transition: transform var(--pdf-turn) cubic-bezier(0.4, 0, 0.2, 1);
}
.pdf-slide-enter-from {
  transform: translateX(100%);
}
.pdf-slide-leave-to {
  transform: translateX(-100%);
}
/* Flip: the old page turns away in the first half, the new one turns in during the second. */
.pdf-flip-leave-active {
  transition: transform calc(var(--pdf-turn) / 2) ease-in;
  backface-visibility: hidden;
}
.pdf-flip-enter-active {
  transition: transform calc(var(--pdf-turn) / 2) ease-out calc(var(--pdf-turn) / 2);
  backface-visibility: hidden;
}
.pdf-flip-leave-to {
  transform: rotateY(90deg);
}
.pdf-flip-enter-from {
  transform: rotateY(-90deg);
}
</style>
